import { LitElement, html } from 'lit';
import { customElement, state, property } from 'lit/decorators.js';
import { styles } from './user-modal.style.js';
import { UserRole } from '@cmms/models';

interface UserFormData {
  name: string;
  email: string;
  password: string;
  role: UserRole;
  company_id: string;
}

@customElement('user-modal')
export class UserModal extends LitElement {
  static styles = styles;
  
  @property({ type: Boolean }) open = false;
  @property({ type: Object }) formData: UserFormData = {
    name: '',
    email: '',
    password: '',
    role: UserRole.SHOP_FLOOR,
    company_id: ''
  };
  @property({ type: Boolean }) isEditing = false;

  @state() private isLoading = false;
  @state() private errorMessage = '';
  @state() private validationErrors: Record<string, string> = {};

  private validateForm(): boolean {
    this.validationErrors = {};

    if (!this.formData.name.trim()) {
      this.validationErrors.name = 'Name is required';
    }

    if (!this.formData.email.trim()) {
      this.validationErrors.email = 'Email is required';
    } else if (!this.isValidEmail(this.formData.email)) {
      this.validationErrors.email = 'Please enter a valid email address';
    }

    if (!this.isEditing && !this.formData.password.trim()) {
      this.validationErrors.password = 'Password is required';
    } else if (this.formData.password && this.formData.password.length < 6) {
      this.validationErrors.password = 'Password must be at least 6 characters';
    }

    if (!this.formData.role) {
      this.validationErrors.role = 'Role is required';
    }

    return Object.keys(this.validationErrors).length === 0;
  }

  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  private handleInputChange(e: Event, field: keyof UserFormData) {
    const target = e.target as any; // Shoelace components
    const value = target.value;
    
    this.formData = {
      ...this.formData,
      [field]: value
    };

    // Clear validation error for this field
    if (this.validationErrors[field]) {
      delete this.validationErrors[field];
      this.validationErrors = { ...this.validationErrors };
    }
  }

  private async handleSave(e?: Event) {
    if (e) {
      e.preventDefault();
    }
    
    if (!this.validateForm()) {
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';

    try {
      const event = new CustomEvent('user-saved', {
        detail: { formData: this.formData },
        bubbles: true,
        composed: true
      });
      this.dispatchEvent(event);
    } catch (error) {
      this.errorMessage = 'Failed to save user';
    } finally {
      this.isLoading = false;
    }
  }

  private handleClose() {
    this.dispatchEvent(new CustomEvent('modal-closed', {
      bubbles: true,
      composed: true
    }));
  }

  private getRoleOptions() {
    return [
      { value: UserRole.ADMIN, label: 'Admin' },
      { value: UserRole.ENGINEER, label: 'Engineer' },
      { value: UserRole.SHOP_FLOOR, label: 'Shop Floor' }
    ];
  }

  render() {
    return html`
      <sl-dialog
        label="${this.isEditing ? 'Edit User' : 'Add New User'}"
        .open=${this.open}
        @sl-hide=${this.handleClose}
      >
        <div class="modal-content">
          ${this.errorMessage ? html`
            <div class="error-message">
              <sl-icon name="exclamation-triangle" style="margin-right: 0.5rem;"></sl-icon>
              ${this.errorMessage}
            </div>
          ` : ''}

          <form @submit=${this.handleSave} 
            @click=${(e: Event) => e.stopPropagation()}
            @sl-input=${(e: Event) => e.stopPropagation()}
            @sl-change=${(e: Event) => e.stopPropagation()}
            @sl-open-change=${(e: Event) => e.stopPropagation()}
            @sl-show=${(e: Event) => e.stopPropagation()}
            @sl-hide=${(e: Event) => e.stopPropagation()}
          >
            <div class="form-group" @click=${(e: Event) => e.stopPropagation()}>
              <label for="name">Full Name *</label>
              <sl-input
                id="name"
                name="name"
                placeholder="Enter full name"
                value=${this.formData.name}
                @sl-input=${(e: Event) => this.handleInputChange(e, 'name')}
                @click=${(e: Event) => e.stopPropagation()}
                ?invalid=${!!this.validationErrors.name}
                required
                size="large"
              >
                <sl-icon name="person" slot="prefix"></sl-icon>
              </sl-input>
              ${this.validationErrors.name ? html`
                <div class="error-message">${this.validationErrors.name}</div>
              ` : ''}
            </div>

            <div class="form-group" @click=${(e: Event) => e.stopPropagation()}>
              <label for="email">Email Address *</label>
              <sl-input
                id="email"
                name="email"
                type="email"
                placeholder="Enter email address"
                value=${this.formData.email}
                @sl-input=${(e: Event) => this.handleInputChange(e, 'email')}
                @click=${(e: Event) => e.stopPropagation()}
                ?invalid=${!!this.validationErrors.email}
                required
                size="large"
              >
                <sl-icon name="envelope" slot="prefix"></sl-icon>
              </sl-input>
              ${this.validationErrors.email ? html`
                <div class="error-message">${this.validationErrors.email}</div>
              ` : ''}
            </div>

            <div class="form-group" @click=${(e: Event) => e.stopPropagation()}>
              <label for="password">Password ${this.isEditing ? '' : '*'}</label>
              <sl-input
                id="password"
                name="password"
                type="password"
                placeholder=${this.isEditing ? 'Leave blank to keep current password' : 'Enter password'}
                value=${this.formData.password}
                @sl-input=${(e: Event) => this.handleInputChange(e, 'password')}
                @click=${(e: Event) => e.stopPropagation()}
                ?invalid=${!!this.validationErrors.password}
                size="large"
              >
                <sl-icon name="lock" slot="prefix"></sl-icon>
              </sl-input>
              ${this.validationErrors.password ? html`
                <div class="error-message">${this.validationErrors.password}</div>
              ` : ''}
              ${this.isEditing ? html`
                <div class="help-text">Leave blank to keep the current password</div>
              ` : ''}
            </div>

            <div class="form-group" @click=${(e: Event) => e.stopPropagation()}>
              <label for="role">Role *</label>
              <sl-select
                id="role"
                name="role"
                value=${this.formData.role}
                @sl-change=${(e: CustomEvent) => {
                  this.handleInputChange(e, 'role');
                  e.stopPropagation();
                }}
                @sl-open-change=${(e: Event) => e.stopPropagation()}
                @sl-show=${(e: Event) => e.stopPropagation()}
                @sl-hide=${(e: Event) => e.stopPropagation()}
                @click=${(e: Event) => e.stopPropagation()}
                ?invalid=${!!this.validationErrors.role}
                required
              >
                <sl-option value="">Select a role</sl-option>
                ${this.getRoleOptions().map(option => html`
                  <sl-option value=${option.value}>${option.label}</sl-option>
                `)}
              </sl-select>
              ${this.validationErrors.role ? html`
                <div class="error-message">${this.validationErrors.role}</div>
              ` : ''}
            </div>
            <div class="form-footer" slot="footer">
              <sl-button
                variant="neutral"
                size="large"
                @click=${this.handleClose}
                ?disabled=${this.isLoading}
              >
                Cancel
              </sl-button>
              <sl-button
                variant="primary"
                size="large"
                @click=${this.handleSave}
                ?loading=${this.isLoading}
                ?disabled=${this.isLoading}
              >
                ${this.isLoading ? 'Saving...' : (this.isEditing ? 'Update User' : 'Create User')}
              </sl-button>
            </div>
          </form>
        </div>
      </sl-dialog>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'user-modal': UserModal;
  }
}
